import { supabase } from '../lib/supabase';
import { CAREER_RESOURCE_SEGMENTS, STATIC_ROUTE_SEGMENTS, UI_LANGUAGES, splitLocalizedPath, type UiLang } from './routes';


export async function findEquivalentSlug(
    currentSlug: string,
    currentLang: string,
    targetLang: string
): Promise<string | null> {
    const currentLangLower = currentLang.toLowerCase();
    const targetLangLower = targetLang.toLowerCase();

    try {
        // Find matching page by its slug in the current language
        const { data: pageMatch } = await supabase
            .from('content_pages')
            .select('slug')
            .eq(`slug->>${currentLangLower}`, currentSlug)
            .limit(1)
            .maybeSingle();

        if (pageMatch && pageMatch.slug) {
            const targetSlug = pageMatch.slug[targetLangLower];
            if (targetSlug) return targetSlug;
        }

        console.log('Slug resolution failed for dynamic page:', currentSlug);
        return null;

    } catch (error) {
        console.error('Error finding equivalent slug:', error);
        return null;
    }
}


const buildReverseSlugMap = (map: Record<string, Record<UiLang, string>>) =>
    UI_LANGUAGES.reduce<Record<string, Record<string, string>>>((acc, lang) => {
        acc[lang] = Object.fromEntries(
            Object.values(map)
                .filter((segments) => segments[lang === 'en' ? 'lt' : 'en'])
                .map((segments) => [
                    segments[lang === 'en' ? 'lt' : 'en'],
                    segments[lang],
                ]),
        );
        return acc;
    }, {});

export const STATIC_SLUG_MAP = buildReverseSlugMap(STATIC_ROUTE_SEGMENTS);
export const CAREER_SLUG_MAP = buildReverseSlugMap(CAREER_RESOURCE_SEGMENTS);


export async function translatePath(
    currentPath: string,
    targetLang: string
): Promise<string> {
    const targetLangLower = targetLang.toLowerCase();
    const { lang: currentLang, relativePath } = splitLocalizedPath(currentPath);
    const segments = relativePath.split('/').filter(Boolean);
    
    if (segments.length === 0) {
        return `/${targetLangLower}`;
    }

    const newSegments = [targetLangLower];
    const staticMapping = STATIC_SLUG_MAP[targetLangLower];
    const currentFirstSlug = segments[0];
    const currentSecondSlug = segments[1];
    const hasSecondSlug = segments.length > 1;
    
    // Translate first slug (top-level page)
    const translatedFirstSlug = staticMapping?.[currentFirstSlug] || currentFirstSlug;
    newSegments.push(translatedFirstSlug);

    // Handle second slug (dynamic content or sub-page)
    if (hasSecondSlug) {
        // Try dynamic resolution
        const currentLangCtx = currentLang || 'en';
        const equivalentSlug = await findEquivalentSlug(currentSecondSlug, currentLangCtx, targetLang);
        
        if (equivalentSlug) {
            newSegments.push(equivalentSlug);
        } else {
            newSegments.push(currentSecondSlug);
        }
    }

    return `/${newSegments.join('/')}`;
}
